import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Plus, Mail, Send, Trash2, Eye, Users, Reply, 
  UserCheck, School, Radio
} from "lucide-react";

const Messages = ({ user }) => {
  const [searchParams] = useSearchParams();
  const [inboxMessages, setInboxMessages] = useState([]);
  const [sentMessages, setSentMessages] = useState([]);
  const [allStaff, setAllStaff] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [activeTab, setActiveTab] = useState("inbox");
  const [isSending, setIsSending] = useState(false);
  
  const [recipientType, setRecipientType] = useState("individual");
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  
  const [formData, setFormData] = useState({
    subject: "",
    message: "",
    channel: "in_app",
  });

  useEffect(() => {
    fetchData();
    
    // Check for compose query params (from Teacher profile)
    const compose = searchParams.get('compose');
    const toId = searchParams.get('to');
    const toName = searchParams.get('name');
    
    if (compose === 'true' && toId) {
      setSelectedRecipients([toId]);
      setRecipientType("individual");
      setIsDialogOpen(true);
    }
  }, [searchParams, activeTab]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [inboxRes, sentRes, staffRes, classesRes] = await Promise.all([
        axios.get(`${API}/messages?box=inbox`),
        axios.get(`${API}/messages?box=sent`),
        axios.get(`${API}/messages/staff`),
        axios.get(`${API}/classes`),
      ]);
      
      setInboxMessages(inboxRes.data);
      setSentMessages(sentRes.data);
      setAllStaff(staffRes.data.filter(s => s.id !== user.id)); // Exclude self
      setClasses(classesRes.data);
    } catch (error) {
      toast.error("Failed to load messages");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subject || !formData.message) {
      toast.error("Please fill in subject and message");
      return;
    }
    
    let finalRecipients = [];
    let messageType = "direct";
    
    if (recipientType === "all_staff") {
      messageType = "broadcast";
      finalRecipients = [];
    } else if (recipientType === "teachers") {
      finalRecipients = allStaff.filter(s => s.role === "teacher").map(s => s.id);
      messageType = finalRecipients.length > 1 ? "group" : "direct";
      
      if (finalRecipients.length === 0) {
        toast.error("No teachers found in the system");
        return;
      }
    } else if (recipientType === "admins") {
      finalRecipients = allStaff.filter(s => s.role === "admin").map(s => s.id);
      messageType = finalRecipients.length > 1 ? "group" : "direct";
      
      if (finalRecipients.length === 0) {
        toast.error("No admins found in the system");
        return;
      }
    } else if (recipientType === "class") {
      if (selectedClasses.length === 0) {
        toast.error("Please select at least one class");
        return;
      }
      
      // Get all teachers assigned to selected classes
      selectedClasses.forEach(classId => {
        const cls = classes.find(c => c.id === classId);
        if (cls && cls.teacher_id && !finalRecipients.includes(cls.teacher_id)) {
          finalRecipients.push(cls.teacher_id);
        }
      });
      
      if (finalRecipients.length === 0) {
        toast.error("Selected classes have no assigned teachers");
        return;
      }
      
      messageType = finalRecipients.length > 1 ? "group" : "direct";
    } else {
      // Individual recipients
      if (selectedRecipients.length === 0) {
        toast.error("Please select at least one recipient");
        return;
      }
      finalRecipients = selectedRecipients;
      messageType = finalRecipients.length > 1 ? "group" : "direct";
    }
    
    try {
      setIsSending(true);
      await axios.post(`${API}/messages`, {
        recipient_ids: finalRecipients,
        subject: formData.subject,
        message: formData.message,
        message_type: messageType,
        channel: formData.channel,
      });
      
      toast.success("✅ Message sent successfully");
      
      // Close modal and reset form
      setIsSending(false);
      setIsDialogOpen(false);
      resetForm();
      
      // Refresh messages
      await fetchData();
    } catch (error) {
      toast.error("Failed to send message");
      console.error(error);
      setIsSending(false);
    }
  };

  const resetForm = () => {
    setFormData({
      subject: "",
      message: "",
      channel: "in_app",
    });
    setSelectedRecipients([]);
    setSelectedClasses([]);
    setRecipientType("individual");
  };

  const handleMarkAsRead = async (messageId) => {
    try {
      // Immediately update local state for instant feedback
      setInboxMessages(prevMessages => 
        prevMessages.map(msg => 
          msg.id === messageId 
            ? { ...msg, is_read: true }
            : msg
        )
      );
      
      // Call API to persist the read state
      await axios.put(`${API}/messages/${messageId}/read`);
      toast.success("Message marked as read", { duration: 1000 });
    } catch (error) {
      // Revert local state on API failure
      setInboxMessages(prevMessages => 
        prevMessages.map(msg => 
          msg.id === messageId 
            ? { ...msg, is_read: false }
            : msg
        )
      );
      toast.error("Failed to mark message as read");
      console.error("Failed to mark as read", error);
    }
  };

  const openMessageDetail = (message) => {
    setSelectedMessage(message);
    setIsDetailDialogOpen(true);
    
    // Mark as read if it's unread and not sent by current user
    // Include broadcast messages in read logic
    if (!message.is_read && (message.sender_id !== user.id || message.message_type === "broadcast")) {
      handleMarkAsRead(message.id);
    }
  };

  const handleRecipientToggle = (recipientId) => {
    if (selectedRecipients.includes(recipientId)) {
      setSelectedRecipients(selectedRecipients.filter(id => id !== recipientId));
    } else {
      setSelectedRecipients([...selectedRecipients, recipientId]);
    }
  };

  const handleClassToggle = (classId) => {
    if (selectedClasses.includes(classId)) {
      setSelectedClasses(selectedClasses.filter(id => id !== classId));
    } else {
      setSelectedClasses([...selectedClasses, classId]);
    }
  };

  const handleReply = (message) => {
    setIsDetailDialogOpen(false);
    setRecipientType("individual");
    setSelectedRecipients([message.sender_id]);
    setFormData({
      subject: `Re: ${message.subject}`,
      message: "",
      channel: "in_app",
    });
    setIsDialogOpen(true);
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm("Are you sure you want to delete this message?")) return;
    
    try {
      await axios.delete(`${API}/messages/${messageId}`);
      toast.success("Message deleted");
      setIsDetailDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete message");
    }
  };

  const unreadCount = inboxMessages.filter(m => !m.is_read && m.sender_id !== user.id).length;

  const getRecipientNames = () => {
    if (recipientType === "all_staff") return "All Staff";
    if (recipientType === "teachers") return "All Teachers";
    if (recipientType === "admins") return "All Admins";
    if (recipientType === "class") {
      return selectedClasses.map(cId => {
        const cls = classes.find(c => c.id === cId);
        return cls?.name;
      }).filter(Boolean).join(", ") || "Select classes";
    }
    return selectedRecipients.map(rId => {
      const staff = allStaff.find(s => s.id === rId);
      return staff?.name;
    }).filter(Boolean).join(", ") || "Select recipients";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="messages-page">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-[#1E624A]">Messages</h2>
          <p className="text-sm text-gray-600">Farriimaha</p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setIsDialogOpen(true);
          }}
          className="bg-[#1E624A] hover:bg-[#164935]"
          data-testid="send-message-btn"
        >
          <Plus size={20} className="mr-2" />
          Send Message
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-white shadow-sm">
          <TabsTrigger
            value="inbox"
            className="data-[state=active]:bg-[#1E624A] data-[state=active]:text-white relative"
            data-testid="inbox-tab"
          >
            📥 Inbox
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center unread-count-badge">
                {unreadCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger
            value="sent"
            className="data-[state=active]:bg-[#1E624A] data-[state=active]:text-white"
            data-testid="sent-tab"
          >
            📤 Sent
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inbox" className="space-y-4 mt-6">
          {inboxMessages.length > 0 ? (
            inboxMessages.map((message) => (
              <div
                key={message.id}
                onClick={() => openMessageDetail(message)}
                className={`message-card rounded-xl p-4 shadow-md hover:shadow-lg transition-all cursor-pointer ${
                  !message.is_read ? "message-unread" : "message-read"
                }`}
                data-testid={`message-${message.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <Mail className="text-[#1E624A] mt-1" size={20} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-semibold text-gray-900">{message.subject}</h3>
                        {!message.is_read && (
                          <Badge className={`bg-[#1E624A] text-white text-xs message-new-badge ${
                            message.is_read ? 'fade-out' : ''
                          }`}>
                            New
                          </Badge>
                        )}
                        {message.message_type === "broadcast" && (
                          <Badge className="bg-[#E4C987] text-[#1E624A] text-xs">
                            <Radio size={12} className="mr-1" />
                            Broadcast
                          </Badge>
                        )}
                        {message.message_type === "group" && (
                          <Badge className="bg-blue-100 text-blue-700 text-xs">
                            <Users size={12} className="mr-1" />
                            Group
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-1">
                        From: <span className="font-medium">{message.sender_name || "Staff"}</span>
                      </p>
                      <p className="text-sm text-gray-600 line-clamp-2">{message.message}</p>
                      <p className="text-xs text-gray-500 mt-2">
                        {new Date(message.created_at).toLocaleDateString()} at{" "}
                        {new Date(message.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                  <Eye className={`${!message.is_read ? 'text-[#1E624A]' : 'text-gray-400'}`} size={18} />
                </div>
              </div>
            ))
          ) : (
            <div className="bg-white rounded-xl p-12 text-center text-gray-500">
              <Mail size={48} className="mx-auto mb-3 text-gray-300" />
              <p>No messages in your inbox</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="sent" className="space-y-4 mt-6">
          {sentMessages.length > 0 ? (
            sentMessages.map((message) => (
              <div
                key={message.id}
                onClick={() => openMessageDetail(message)}
                className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition-all cursor-pointer"
                data-testid={`sent-message-${message.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <Send className="text-blue-500 mt-1" size={20} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-semibold text-gray-900">{message.subject}</h3>
                        {message.message_type === "broadcast" && (
                          <Badge className="bg-[#E4C987] text-[#1E624A] text-xs">
                            <Radio size={12} className="mr-1" />
                            Broadcast
                          </Badge>
                        )}
                        {message.message_type === "group" && (
                          <Badge className="bg-blue-100 text-blue-700 text-xs">
                            <Users size={12} className="mr-1" />
                            Group
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2">{message.message}</p>
                      <p className="text-xs text-gray-500 mt-2">
                        {new Date(message.created_at).toLocaleDateString()} at{" "}
                        {new Date(message.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="bg-white rounded-xl p-12 text-center text-gray-500">
              <Send size={48} className="mx-auto mb-3 text-gray-300" />
              <p>No sent messages</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Send Message Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-h-[85vh] flex flex-col p-0 gap-0 max-w-2xl">
          <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gradient-to-r from-[#1E624A] to-[#266653] text-white">
            <DialogTitle className="text-xl flex items-center gap-2">
              <Send size={24} />
              Send New Message
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="overflow-y-auto px-6 py-4 space-y-4 flex-1">
              {/* Recipient Type Selector */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">To *</Label>
                <Select value={recipientType} onValueChange={setRecipientType}>
                  <SelectTrigger data-testid="recipient-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_staff">
                      <div className="flex items-center gap-2">
                        <Radio size={16} />
                        All Staff (Broadcast)
                      </div>
                    </SelectItem>
                    <SelectItem value="teachers">
                      <div className="flex items-center gap-2">
                        <Users size={16} />
                        All Teachers
                      </div>
                    </SelectItem>
                    <SelectItem value="admins">
                      <div className="flex items-center gap-2">
                        <UserCheck size={16} />
                        All Admins
                      </div>
                    </SelectItem>
                    <SelectItem value="class">
                      <div className="flex items-center gap-2">
                        <School size={16} />
                        By Class
                      </div>
                    </SelectItem>
                    <SelectItem value="individual">
                      <div className="flex items-center gap-2">
                        <UserCheck size={16} />
                        Individual Staff
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Recipient Selection Preview */}
              <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                <span className="font-medium">Selected: </span>
                {getRecipientNames()}
              </div>

              {/* Individual Recipients */}
              {recipientType === "individual" && (
                <div className="space-y-2">
                  <Label className="text-sm text-gray-600">Select Recipients</Label>
                  <div className="border rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto bg-white">
                    {allStaff.map((staff) => (
                      <div key={staff.id} className="flex items-center gap-2">
                        <Checkbox
                          checked={selectedRecipients.includes(staff.id)}
                          onCheckedChange={() => handleRecipientToggle(staff.id)}
                          data-testid={`recipient-${staff.id}`}
                        />
                        <label className="text-sm text-gray-700 flex-1 cursor-pointer">
                          {staff.name} 
                          <span className="text-xs text-gray-500 ml-2">
                            ({staff.role === "admin" ? "Admin" : "Teacher"})
                          </span>
                        </label>
                      </div>
                    ))}
                    {allStaff.length === 0 && (
                      <p className="text-sm text-gray-500">No staff available</p>
                    )}
                  </div>
                </div>
              )}

              {/* Class Recipients */}
              {recipientType === "class" && (
                <div className="space-y-2">
                  <Label className="text-sm text-gray-600">Select Classes (sends to assigned teachers)</Label>
                  <div className="border rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto bg-white">
                    {classes.map((cls) => (
                      <div key={cls.id} className="flex items-center gap-2">
                        <Checkbox
                          checked={selectedClasses.includes(cls.id)}
                          onCheckedChange={() => handleClassToggle(cls.id)}
                          data-testid={`class-${cls.id}`}
                        />
                        <label className="text-sm text-gray-700 flex-1 cursor-pointer">
                          {cls.name} - {cls.grade}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Subject */}
              <div className="space-y-2">
                <Label htmlFor="subject" className="text-sm font-medium">Subject *</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Enter subject"
                  required
                  data-testid="message-subject"
                />
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message" className="text-sm font-medium">Message *</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Type your message here..."
                  rows={6}
                  required
                  data-testid="message-content"
                />
              </div>

              {/* Channel */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Channel</Label>
                <Select 
                  value={formData.channel} 
                  onValueChange={(value) => setFormData({ ...formData, channel: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_app">📱 In-App</SelectItem>
                    <SelectItem value="email" disabled>✉️ Email (Coming Soon)</SelectItem>
                    <SelectItem value="whatsapp" disabled>📲 WhatsApp (Coming Soon)</SelectItem>
                    <SelectItem value="sms" disabled>💬 SMS (Coming Soon)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border-t bg-gray-50 px-6 py-4 flex justify-end gap-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsDialogOpen(false)}
                disabled={isSending}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-[#1E624A] hover:bg-[#164935]"
                disabled={isSending}
                data-testid="send-message-submit"
              >
                {isSending ? (
                  <>
                    <div className="spinner-small mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={16} className="mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Message Detail Dialog */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#1E624A] flex items-center gap-2">
              <Mail size={24} />
              {selectedMessage?.subject}
            </DialogTitle>
          </DialogHeader>
          {selectedMessage && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-gray-600 flex-wrap">
                <span className="font-medium">From:</span>
                <span>{selectedMessage.sender_id === user.id ? "You" : selectedMessage.sender_name || "Staff"}</span>
                {selectedMessage.message_type === "broadcast" && (
                  <Badge className="bg-[#E4C987] text-[#1E624A] text-xs">
                    <Radio size={12} className="mr-1" />
                    Broadcast
                  </Badge>
                )}
                {selectedMessage.message_type === "group" && (
                  <Badge className="bg-blue-100 text-blue-700 text-xs">
                    <Users size={12} className="mr-1" />
                    Group Message
                  </Badge>
                )}
              </div>
              <div className="text-sm text-gray-600">
                <span className="font-medium">Date:</span>{" "}
                {new Date(selectedMessage.created_at).toLocaleDateString()} at{" "}
                {new Date(selectedMessage.created_at).toLocaleTimeString()}
              </div>
              <div className="border-t pt-4">
                <p className="text-gray-700 whitespace-pre-wrap">{selectedMessage.message}</p>
              </div>
            </div>
          )}
          <div className="flex justify-between gap-3 mt-4">
            <div className="flex gap-2">
              {selectedMessage && selectedMessage.sender_id !== user.id && (
                <Button 
                  variant="outline" 
                  onClick={() => handleReply(selectedMessage)}
                  className="text-[#1E624A] border-[#1E624A]"
                  data-testid="reply-button"
                >
                  <Reply size={16} className="mr-2" />
                  Reply
                </Button>
              )}
              {selectedMessage && selectedMessage.sender_id === user.id && (
                <Button 
                  variant="outline" 
                  onClick={() => handleDeleteMessage(selectedMessage.id)}
                  className="text-red-600 border-red-600"
                  data-testid="delete-message-button"
                >
                  <Trash2 size={16} className="mr-2" />
                  Delete
                </Button>
              )}
            </div>
            <Button variant="outline" onClick={() => setIsDetailDialogOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Messages;
